import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  LocalHospital,
  Add as AddIcon,
  History,
  PersonSearch
} from '@mui/icons-material';
import { ipdAPI } from '../services/api';
import { useNavigate } from 'react-router-dom';

function NursingDashboard() {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadActivePatients();
  }, []);

  const loadActivePatients = async () => {
    try {
      const response = await ipdAPI.getActiveAdmissions();
      const data = response.data.data || response.data;
      setPatients(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load active patients:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRecordVitals = (patient) => {
    navigate(`/nursing/vitals/${patient.id}`, {
      state: {
        patientId: patient.patient_id,
        patientName: `${patient.first_name} ${patient.last_name}`,
        patientCode: patient.patient_code,
        roomNumber: patient.room_number,
        admissionDate: patient.admission_date,
        admissionId: patient.id
      }
    });
  };

  const handleViewHistory = (patient) => {
    navigate(`/nursing/vitals/history/${patient.id}`, {
      state: {
        patientName: `${patient.first_name} ${patient.last_name}`,
        patientCode: patient.patient_code,
        roomNumber: patient.room_number
      }
    });
  };

  const getDaysAdmitted = (admissionDate) => {
    const admitted = new Date(admissionDate);
    const today = new Date();
    const diffTime = Math.abs(today - admitted);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Card elevation={2}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <LocalHospital sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h5" component="h2" fontWeight="bold">
                Nursing Dashboard
              </Typography>
              <Typography variant="body2" color="text.secondary">
                IPD Patient Vitals Management
              </Typography>
            </Box>
          </Box>

          {patients.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <PersonSearch sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No active IPD patients
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Admitted patients will appear here
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'primary.main' }}>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Patient Code</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Patient Name</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Room</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Admission Date</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Days</TableCell>
                    <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {patients.map((patient) => (
                    <TableRow key={patient.id} hover>
                      <TableCell>
                        <Chip label={patient.patient_code} size="small" color="primary" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {patient.first_name} {patient.last_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {patient.gender} • {patient.age} years
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{patient.room_number}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {patient.category_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {new Date(patient.admission_date).toLocaleDateString('en-GB')}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={`Day ${getDaysAdmitted(patient.admission_date)}`}
                          size="small"
                          color="info"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Record Vitals">
                          <IconButton
                            color="primary"
                            onClick={() => handleRecordVitals(patient)}
                            size="small"
                          >
                            <AddIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="View Vitals History">
                          <IconButton
                            color="secondary"
                            onClick={() => handleViewHistory(patient)}
                            size="small"
                          >
                            <History />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

export default NursingDashboard;